import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Header from 'components/Header'
import Footer from 'components/Footer'
import CovidBanner from 'components/CovidBanner'
import ShippingInfo from 'pages/ShippingInfo'
import RecipientInfo from 'pages/RecipientInfo'
import ExpiredVerificationLink from 'pages/ExpiredVerificationLink'
import NotFound from 'pages/NotFound'
import Locker from 'pages/Locker'
import CansDoorKnocker from 'pages/CansDoorKnocker'
import SupportPage from 'pages/SupportPage'
import { useTranslation } from 'react-i18next'
import SubscribePYWPage from 'pages/Registration/Registration'
import LoginForm from 'pages/LoginForm'
import HomePage from 'pages/HomePage'
import PYWVerificationPage from 'pages/EmailVerification/EmailVerification'
import Unsubscribe from 'pages/Unsubscribe/Unsubscribe'
import ForgotEmailForm from 'pages/ForgotEmailForm/ForgotEmailForm'
import { selectIsAuthenticated, selectShippingInfo } from 'store/shipping/shippingSelectors'
import { useSelector } from 'react-redux'
import ProtectedRoute from './ProtectedRoute'
import GuestRoute from './GuestRoute'

/* eslint-disable react/prop-types */
const AppTemplate = ({
  component,
  hideFaqs,
  hideCovidBanner,
  disableNavigation,
  isAuthenticated,
  showRedDot,
}) => {
  const { t } = useTranslation('translation')
  const shippingInfo = useSelector(selectShippingInfo)
  const shouldHideCovidBanner = hideCovidBanner || (shippingInfo && shippingInfo.isRecipientProfile)

  return (
    <div className="app">
      <a style={{ position: 'absolute' }} aria-label="app" href="#main">
        {t('skip_to_main')}
      </a>
      <header className="app-header">
        <Header
          disableNavigation={disableNavigation}
          isAuthenticated={isAuthenticated}
          showRedDot={showRedDot}
        />
      </header>
      <main id="main" className="app-content">
        {!shouldHideCovidBanner && <CovidBanner />}
        {component}
      </main>
      <footer className="app-footer">
        <Footer hideFaqs={hideFaqs} />
      </footer>
    </div>
  )
}

const Router = () => {
  const { i18n } = useTranslation('translation')
  const isAuthenticated = useSelector(selectIsAuthenticated)

  return (
    <div>
      <Routes>
        <Route path="/" element={<Navigate replace to="/rs/tr/en" />} />
        <Route path="*" element={<Navigate replace to="/rs/tr/en" />} />
        <Route path="/rs/tr/:lang/*" element={<AppTemplate component={<NotFound />} />} />
        <Route
          path="/rs/tr/:lang/:pin"
          element={
            isAuthenticated ? (
              <GuestRoute
                element={
                  <AppTemplate component={<ShippingInfo />} disableNavigation isAuthenticated />
                }
              />
            ) : (
              <GuestRoute element={<AppTemplate component={<ShippingInfo />} showRedDot />} />
            )
          }
        />
        <Route
          path="/rs/tr/:lang/support"
          element={
            isAuthenticated ? (
              <AppTemplate
                hideCovidBanner
                component={<SupportPage />}
                disableNavigation
                isAuthenticated
              />
            ) : (
              <AppTemplate hideCovidBanner component={<SupportPage />} />
            )
          }
        />
        <Route
          exact
          path="/locker/qrcode/:lang/:pickupCode/:shipmentPin"
          element={<AppTemplate component={<Locker />} hideFaqs hideCovidBanner />}
        />
        <Route
          exact
          path="/cans/dk/:lang/:pinAndRetailCode"
          element={<AppTemplate component={<CansDoorKnocker />} hideFaqs hideCovidBanner />}
        />
        <Route
          path="/cansAndLocker/dk/:lang/support"
          element={<AppTemplate hideCovidBanner hideFaqs component={<SupportPage />} />}
        />
        <Route path="/rs/tr/:lang/subscriptions" element={<SubscribePYWPage />} />
        <Route
          path="/rs/tr/:lang/login"
          element={
            isAuthenticated ? (
              <Navigate to={`/rs/tr/${i18n.language}/recipient`} replace />
            ) : (
              <AppTemplate component={<LoginForm />} disableNavigation />
            )
          }
        />
        <Route
          path="/rs/tr/:lang/home"
          element={<AppTemplate component={<HomePage />} disableNavigation />}
        />

        <Route path="/email/verification/:lang/verify" element={<PYWVerificationPage />} />
        <Route path="/email/notification/:lang/unsubscribe" element={<Unsubscribe />} />
        <Route
          path="/rs/tr/:lang/:pin/verifyLinkExpired"
          element={<AppTemplate component={<ExpiredVerificationLink />} disableNavigation />}
        />
        <Route
          path="/rs/tr/:lang/register"
          element={<AppTemplate hideCovidBanner hideFaqs component={<SubscribePYWPage />} />}
        />
        <Route
          path="/rs/tr/:lang/forgotemail"
          element={
            <AppTemplate
              component={<ForgotEmailForm />}
              disableNavigation
              hideFaqs
              hideCovidBanner
            />
          }
        />
        <Route
          path="/rs/tr/:lang/recipient"
          element={
            <ProtectedRoute
              element={
                <AppTemplate component={<RecipientInfo />} disableNavigation isAuthenticated />
              }
            />
          }
        />
      </Routes>
    </div>
  )
}

export default Router
